import React, { FC, FormEvent, ReactNode, useState } from 'react';
import { Form as FormikForm, Formik, FormikProps } from 'formik';
import { Divider, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { find, get } from 'lodash';
import moment from 'moment';

import {
  ContentCommentary,
  ContentSharePreviewFragment,
  Network,
  ShareContentDestination,
  ShareContentInput,
  SharedStatus,
  ShareType,
  useShareContentMutation,
  ShareContentMutation,
} from 'api';
import { useContent, useNotify, useSelectedNetworks, useSocialConnections } from 'hooks';
import { FACEBOOK_LIMIT } from 'app-constants';

import { IShare, IShareNetworkAuth } from '../types';
import { SharingSchema } from '../validations';
import Networks from './Networks';
import { Scheduler, ShareButton } from '../components';
import Preview from './Preview';
import { isPhotoOnly } from '../../content/helpers';
import { FACEBOOK_ID } from '../constants';

interface IProps {
  previewInfo: ContentSharePreviewFragment;
  heading: ReactNode;
  onConnect(): void;
  onClose(): void;
}

interface IVariables {
  content_id: string;
  items: ShareContentInput[];
  timezone?: string;
}

const useStyles = makeStyles(theme => ({
  container: {
    paddingTop: theme.spacing(4),
  },
  content: {
    position: 'relative',
    paddingLeft: theme.spacing(10),
    paddingRight: theme.spacing(10),
    paddingTop: theme.spacing(5),
    backgroundColor: theme.palette.background.default,
  },
  divider: {
    marginTop: theme.spacing(5),
    height: 3,
  },
}));

const Form: FC<IProps> = ({ previewInfo, onClose, onConnect, heading }) => {
  const classes = useStyles();
  const content = useContent();
  const notify = useNotify();
  const connections = useSocialConnections();
  const { selectedNetworks } = useSelectedNetworks();

  const [working, setWorking] = useState(false);
  const [values, setValues] = useState(null);
  const [timezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || null);

  const initialValues = getInitialValues();
  const isInitialValid = SharingSchema(initialValues, content).isValidSync(initialValues);

  const { mutate } = useShareContentMutation({
    onError: handleError,
    onSuccess: handleSuccess,
  });

  function getAccountId(provider: Network) {
    const hasSelectedNetworks = selectedNetworks && !!selectedNetworks.length;
    const defaultAccount = connections.find(conn => conn.provider === provider);

    if (!hasSelectedNetworks) {
      return defaultAccount && defaultAccount.id;
    }

    const selectedAccount = selectedNetworks.find(selectedNetwork => {
      if (!selectedNetwork || typeof selectedNetwork === 'string') {
        return false;
      }
      return selectedNetwork.provider === provider;
    });

    if (typeof selectedAccount === 'string') {
      return undefined;
    }

    if (!selectedAccount) {
      return defaultAccount && defaultAccount.id;
    }

    return selectedAccount && selectedAccount.id;
  }

  function getCommentary(suggested: ContentCommentary) {
    const commentary = ((suggested && suggested.commentary) || '').trim();
    return commentary || '';
  }

  function getInitialValues(): IShare {
    const shareType = ShareType.Now;
    const shareAt: IShare['share_at'] = null;

    return {
      content_id: content.content_id,
      share_type: shareType,
      share_at: shareAt,
      twitter: getNetworkInitialValues(Network.Twitter),
      linkedin: getNetworkInitialValues(Network.Linkedin),
      facebook: getFacebookInitialValues(),
    };
  }

  function getNetworkInitialValues(network: Network): IShareNetworkAuth {
    const { allowed_counts, suggested_commentary } = previewInfo;

    const suggested = find(suggested_commentary, { network });
    const leadingAccountId = getAccountId(network);
    const needsNewShare = suggested && leadingAccountId;
    const hasSelectedNetworks = !!selectedNetworks.length;

    const networkInitialValues: IShareNetworkAuth = {
      active: false,
      limit: allowed_counts[network],
      network_id: leadingAccountId,
      user_commentary: '',
    };

    if (hasSelectedNetworks) {
      networkInitialValues.active = !!find(selectedNetworks, ['id', leadingAccountId]);
      networkInitialValues.user_commentary = getCommentary(suggested);
    } else if (needsNewShare) {
      networkInitialValues.active = true;
      networkInitialValues.user_commentary = getCommentary(suggested);
    }

    return networkInitialValues;
  }

  function getFacebookInitialValues() {
    const { suggested_commentary } = previewInfo;
    const suggested = find(suggested_commentary, { network: Network.Facebook });
    const isPhotoOnlyContent = isPhotoOnly(content);

    return {
      active: selectedNetworks?.includes(FACEBOOK_ID) && !isPhotoOnlyContent,
      limit: FACEBOOK_LIMIT,
      user_commentary: getCommentary(suggested),
    };
  }

  function handleError() {
    setWorking(false);
    setValues(null);
    notify.mutationError();
  }

  function handleSuccess(data: ShareContentMutation) {
    const { share_type, share_at } = values;
    const { shareContent } = data;
    const items = shareContent.results;
    const hasFailure = items.some(item => item.status === SharedStatus.Failed);

    if (hasFailure) {
      notify.error({ i18n: { i18nKey: 'sharing:shareFailure' } });
      setWorking(false);
      setValues(null);
      onClose();
      return;
    }

    let i18nKey = 'sharing:shareSuccess';
    const date = moment.unix(share_at).format('dddd, MMMM Do, YYYY [at] h:mma');

    switch (share_type) {
      case ShareType.Scheduled:
        i18nKey = 'sharing:scheduledSuccess';
        break;
      case ShareType.Auto:
        i18nKey = 'sharing:autoSuccess';
        break;
    }

    notify.info({
      i18n: {
        i18nKey,
        values: { date },
      },
    });

    setWorking(false);
    setValues(null);
    onClose();
  }

  function shareToTwitterAndLinkedin(formValues: IShare) {
    const { content_id, share_at, share_type, twitter, linkedin } = formValues;
    const items: ShareContentInput[] = [];
    const common = { share_type, share_at };

    if (twitter.active) {
      const { user_commentary, network_id } = twitter;
      items.push({ user_commentary, network_id, ...common });
    }

    if (linkedin.active) {
      const { user_commentary, network_id } = linkedin;
      items.push({ user_commentary, network_id, ...common });
    }

    if (!items.length) {
      return;
    }

    const variables: IVariables = {
      content_id,
      items,
      timezone,
    };

    setWorking(true);
    setValues(formValues);

    mutate(variables);
  }

  function shareToFacebook(formValues: IShare) {
    const { content_id, facebook, twitter, linkedin } = formValues;
    if (!facebook.active) {
      return;
    }
    const { shortened_urls } = previewInfo;
    const { user_commentary } = facebook;

    if (!shortened_urls) {
      return;
    }

    window?.FB?.ui(
      {
        method: 'share',
        href: `${shortened_urls.facebook.short_link}`,
        quote: user_commentary,
      },
      () => {
        const variables = {
          content_id,
          items: [
            {
              share_type: ShareType.Frontend,
              destination: ShareContentDestination.Facebook,
            },
          ],
          timezone,
        };

        mutate(variables);

        // Need to handle onClose here because of the callback.
        if (!twitter.active && !linkedin.active) {
          onClose();
        }
      }
    );
  }

  function handleFormikSubmit(shareValues: IShare) {
    shareToTwitterAndLinkedin(shareValues);
    shareToFacebook(shareValues);
  }

  return (
    <Formik
      enableReinitialize
      onSubmit={handleFormikSubmit}
      initialValues={initialValues}
      isInitialValid={isInitialValid}
      validationSchema={SharingSchema(undefined, content)}
    >
      {({ isValid, handleSubmit, values: formikValues }: FormikProps<IShare>) => {
        const formProps = {
          onSubmit: (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            event.stopPropagation();
            handleSubmit(event);
          },
        };

        const isShareable =
          get(formikValues, 'twitter.active') ||
          get(formikValues, 'linkedin.active') ||
          get(formikValues, 'facebook.active');

        return (
          <FormikForm {...formProps}>
            {heading}
            <Grid container className={classes.container} spacing={3}>
              <Grid lg={8} xs={12} item>
                <Networks onConnect={onConnect} previewInfo={previewInfo} />
                <Scheduler />
              </Grid>
              <Grid lg={4} xs={12} item>
                <Preview />
              </Grid>
            </Grid>
            <Divider className={classes.divider} />
            <Grid container justify="flex-end">
              <Grid item>
                <ShareButton
                  i18nKey="common:share"
                  disabled={!isValid || !isShareable}
                  working={working}
                />
              </Grid>
            </Grid>
          </FormikForm>
        );
      }}
    </Formik>
  );
};

export default Form;
